import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Copy,
  ExternalLink,
  GripVertical,
  Globe,
  Link2,
  Lock,
  Pencil,
  Share2,
  Trash2,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Portal, PortalGenieSummary } from '@/types/portal/portal';
import { DEFAULT_BRANDING, GenieBranding } from '@/types/ui/branding';
import { PortalService } from '@/services/database/PortalService';

interface PortalCardProps {
  portal: Portal;
  genieCount?: number;
  geniesSummary?: PortalGenieSummary[];
  onEdit: (portal: Portal) => void;
  onDelete: (portal: Portal) => void;
  isDragEnabled?: boolean;
  /** Account-level branding to use as fallback when portal has no custom branding */
  accountBranding?: GenieBranding | null;
}

/** Resolve branding: portal → account → default */
const resolveBranding = (
  portalBranding: GenieBranding | null,
  accountBranding?: GenieBranding | null,
): GenieBranding => {
  if (portalBranding && portalBranding.primaryColor) return portalBranding;
  if (accountBranding && accountBranding.primaryColor) return accountBranding;
  return DEFAULT_BRANDING;
};

/** Build CSS gradient string from branding */
const buildGradient = (b: GenieBranding, angle?: number): string => {
  const deg = angle ?? b.gradientAngle ?? 135;
  if (b.gradientEnabled && b.secondaryColor) {
    return `linear-gradient(${deg}deg, ${b.primaryColor}, ${b.secondaryColor})`;
  }
  // Single color with a subtle lighter shade
  return `linear-gradient(${deg}deg, ${b.primaryColor}, ${b.primaryColor}dd)`;
};

const PortalCard = ({
  portal,
  genieCount = 0,
  geniesSummary = [],
  onEdit,
  onDelete,
  isDragEnabled = false,
  accountBranding,
}: PortalCardProps) => {
  const navigate = useNavigate();
  const branding = resolveBranding(portal.branding, accountBranding);
  const logoUrl = branding.logoUrl || branding.customAvatarUrl;
  const gradient = buildGradient(branding);

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: portal.id,
    disabled: !isDragEnabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleShare = async () => {
    try {
      const shareUrl = PortalService.generateShareUrl(portal);
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Portal link copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleCopyEmbed = async () => {
    try {
      const embedCode = PortalService.generateEmbedCode(portal);
      await navigator.clipboard.writeText(embedCode);
      toast.success('Embed code copied to clipboard!');
    } catch (error) {
      toast.error('Failed to copy embed code');
    }
  };

  const handleViewPortal = () => {
    window.open(`/portal/${portal.slug}`, '_blank');
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-2xl shadow-lg hover:shadow-xl transition-shadow overflow-hidden bg-white border border-gray-200/60"
    >
      <div className="flex">
        {/* Branded Visual Panel */}
        <div
          className="relative w-48 min-h-[180px] flex-shrink-0 flex flex-col items-center justify-center p-4"
          style={{ background: gradient }}
        >
          {/* Subtle pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.08]"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
              backgroundSize: '24px 24px, 32px 32px',
            }}
          />

          {/* Drag Handle (top-left) */}
          {isDragEnabled && (
            <button
              {...attributes}
              {...listeners}
              className="absolute top-2 left-2 p-1 hover:bg-white/20 rounded transition-colors cursor-grab active:cursor-grabbing"
              title="Drag to reorder"
            >
              <GripVertical className="w-4 h-4 text-white/70" />
            </button>
          )}

          {/* Genie Avatars or Portal Initial */}
          <div className="relative z-10 flex flex-col items-center gap-3">
            {geniesSummary.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-2 max-w-[160px]">
                {geniesSummary.slice(0, 5).map((genie) => (
                  <div
                    key={genie.agent_id}
                    className="w-11 h-11 rounded-full bg-white/90 shadow-sm flex items-center justify-center overflow-hidden ring-2 ring-white/30"
                    title={genie.name}
                  >
                    {genie.logoUrl ? (
                      <img
                        src={genie.logoUrl}
                        alt={genie.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span
                        className="text-sm font-bold"
                        style={{ color: genie.primaryColor || branding.primaryColor }}
                      >
                        {genie.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                ))}
                {genieCount > 5 && (
                  <div className="w-11 h-11 rounded-full bg-white/30 backdrop-blur-sm shadow-sm flex items-center justify-center ring-2 ring-white/30">
                    <span className="text-xs font-bold text-white">+{genieCount - 5}</span>
                  </div>
                )}
              </div>
            ) : logoUrl ? (
              <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm p-2 flex items-center justify-center shadow-sm">
                <img
                  src={logoUrl}
                  alt={portal.name}
                  className="w-full h-full object-contain rounded-lg"
                />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-sm">
                <span className="text-2xl font-bold text-white">
                  {portal.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}

            {/* Genie count chip */}
            <div className="flex items-center gap-1.5 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
              <Users className="w-3.5 h-3.5 text-white" />
              <span className="text-xs font-semibold text-white">
                {genieCount} genie{genieCount !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
        </div>

        {/* Card Content */}
        <div className="flex-1 p-5 min-w-0">
          {/* Top Row: Badges + Actions */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {portal.is_public && portal.is_active ? (
                <Badge className="bg-[#4E9CFF]/10 text-[#4E9CFF] border border-[#4E9CFF]/20 hover:bg-[#4E9CFF]/10">
                  <Globe className="w-3 h-3 mr-1" />
                  Public
                </Badge>
              ) : !portal.is_active ? (
                <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                  Inactive
                </Badge>
              ) : (
                <Badge variant="secondary" className="bg-gray-100 text-gray-600">
                  Private
                </Badge>
              )}
              {portal.password_protected && (
                <Badge variant="secondary" className="bg-amber-50 text-amber-700 border border-amber-200">
                  <Lock className="w-3 h-3 mr-1" />
                  Protected
                </Badge>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleViewPortal}
                className="text-gray-600 hover:text-gray-900"
              >
                <ExternalLink className="w-4 h-4 mr-1.5" />
                Preview
              </Button>
              <Button
                size="sm"
                onClick={() => navigate(`/genie-hub/portals/${portal.id}`)}
                className="text-white hover:opacity-90"
                style={{ background: gradient }}
              >
                <Pencil className="w-4 h-4 mr-1.5" />
                Edit Portal
              </Button>
            </div>
          </div>

          {/* Portal Info */}
          <div className="mb-3">
            <h3 className="text-xl font-bold text-gray-900 mb-0.5">{portal.name}</h3>
            <div className="flex items-center gap-1.5 text-sm text-gray-500">
              <Link2 className="w-3.5 h-3.5" />
              <span className="font-mono">/{portal.slug}</span>
            </div>
          </div>

          {portal.description && (
            <p className="text-sm text-gray-600 line-clamp-2 mb-3">{portal.description}</p>
          )}

          {/* Bottom Row: Quick info + actions */}
          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-center gap-3 text-sm">
              <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 rounded-md px-2 py-0.5 font-medium">
                {portal.layout_config?.columns || 3} Col Grid
              </span>
              {portal.embed_allowed && (
                <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-md px-2 py-0.5 font-medium">
                  Embed On
                </span>
              )}
              {portal.welcome_message && (
                <span className="text-gray-600 truncate max-w-[220px] italic" title={portal.welcome_message}>
                  "{portal.welcome_message}"
                </span>
              )}
            </div>

            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700 h-8 px-2"
                onClick={handleShare}
                title="Copy link"
              >
                <Share2 className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700 h-8 px-2"
                onClick={handleCopyEmbed}
                title="Copy embed code"
              >
                <Copy className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 px-2"
                onClick={() => onDelete(portal)}
                title="Delete portal"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalCard;
